import React, { useState, useEffect } from 'react';
import { adminAPI } from '../utils/api';
import './Models.css';

const PROVIDERS = ['google', 'openai', 'claude', 'groq', 'perplexity'];
const TIERS = ['free', 'paid'];
const SPEEDS = ['very_fast', 'fast', 'medium', 'slow'];
const CAPABILITIES = ['text', 'vision', 'audio', 'reasoning', 'coding', 'synthesis'];

function Models() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterProvider, setFilterProvider] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingModel, setEditingModel] = useState(null);
  const [creatingModel, setCreatingModel] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getModels({ includeAll: true });
      setModels(data.models || data || []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch models');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (model) => {
    setActionLoading(model.id);
    try {
      if (model.is_active) {
        await adminAPI.deactivateModel(model.id);
      } else {
        await adminAPI.activateModel(model.id);
      }
      await fetchModels();
    } catch (err) {
      console.error('Failed to toggle model status:', err);
      alert('Failed to update model status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteModel = async (model) => {
    if (!confirm(`Are you sure you want to delete "${model.name}"? This action cannot be undone.`)) {
      return;
    }
    setActionLoading(model.id);
    try {
      await adminAPI.deleteModel(model.id);
      await fetchModels();
    } catch (err) {
      console.error('Failed to delete model:', err);
      alert('Failed to delete model');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredModels = models.filter(model => {
    if (filterProvider && model.provider !== filterProvider) return false;
    if (filterTier && model.tier !== filterTier) return false;
    if (filterStatus === 'active' && !model.is_active) return false;
    if (filterStatus === 'inactive' && model.is_active) return false;
    if (filterStatus === 'deprecated' && !model.is_deprecated) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        model.name?.toLowerCase().includes(search) ||
        model.id?.toLowerCase().includes(search) ||
        model.provider?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const groupedModels = filteredModels.reduce((acc, model) => {
    const provider = model.provider || 'unknown';
    if (!acc[provider]) acc[provider] = [];
    acc[provider].push(model);
    return acc;
  }, {});

  const stats = {
    total: models.length,
    active: models.filter(m => m.is_active).length,
    inactive: models.filter(m => !m.is_active).length,
    deprecated: models.filter(m => m.is_deprecated).length,
  };

  if (loading) {
    return (
      <div className="models">
        <div className="loading">
          <div className="spinner"></div>
          <span>Loading models...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="models">
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">LLM Models</h1>
          <p className="page-subtitle">Manage AI models, rate limits, and availability</p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreatingModel(true)}>
          <PlusIcon /> Add Model
        </button>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={fetchModels}>Retry</button>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Models</div>
          <div className="stat-value">{stats.total}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active</div>
          <div className="stat-value" style={{ color: 'var(--success-color)' }}>{stats.active}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Inactive</div>
          <div className="stat-value" style={{ color: 'var(--text-secondary)' }}>{stats.inactive}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Deprecated</div>
          <div className="stat-value" style={{ color: 'var(--warning-color)' }}>{stats.deprecated}</div>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-container">
          <SearchIcon className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search models..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="filter-select"
          value={filterProvider}
          onChange={(e) => setFilterProvider(e.target.value)}
        >
          <option value="">All Providers</option>
          {PROVIDERS.map(p => (
            <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
          ))}
        </select>
        <select
          className="filter-select"
          value={filterTier}
          onChange={(e) => setFilterTier(e.target.value)}
        >
          <option value="">All Tiers</option>
          <option value="free">Free</option>
          <option value="paid">Paid</option>
        </select>
        <select
          className="filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="deprecated">Deprecated</option>
        </select>
      </div>

      <div className="models-list">
        {Object.keys(groupedModels).length === 0 ? (
          <div className="no-results">No models found matching your criteria</div>
        ) : (
          Object.entries(groupedModels).map(([provider, providerModels]) => (
            <div key={provider} className="provider-section">
              <h2 className="provider-header">
                <ProviderIcon provider={provider} />
                {provider.charAt(0).toUpperCase() + provider.slice(1)}
                <span className="provider-count">{providerModels.length} models</span>
              </h2>
              <div className="models-grid">
                {providerModels.map(model => (
                  <ModelCard
                    key={model.id}
                    model={model}
                    onEdit={() => setEditingModel(model)}
                    onToggleActive={() => handleToggleActive(model)}
                    onDelete={() => handleDeleteModel(model)}
                    isLoading={actionLoading === model.id}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {editingModel && (
        <EditModelModal
          model={editingModel}
          onClose={() => setEditingModel(null)}
          onSave={async (updatedData) => {
            try {
              await adminAPI.updateModel(editingModel.id, updatedData);
              await fetchModels();
              setEditingModel(null);
            } catch (err) {
              console.error('Failed to update model:', err);
              alert('Failed to update model');
            }
          }}
        />
      )}

      {creatingModel && (
        <CreateModelModal
          onClose={() => setCreatingModel(false)}
          onSave={async (modelData) => {
            try {
              await adminAPI.createModel(modelData);
              await fetchModels();
              setCreatingModel(false);
            } catch (err) {
              console.error('Failed to create model:', err);
              alert('Failed to create model: ' + (err.response?.data?.detail || err.message));
            }
          }}
        />
      )}
    </div>
  );
}

function ModelCard({ model, onEdit, onToggleActive, onDelete, isLoading }) {
  const capabilities = model.capabilities || [];

  return (
    <div className={`model-card ${!model.is_active ? 'inactive' : ''} ${model.is_deprecated ? 'deprecated' : ''}`}>
      <div className="model-card-header">
        <div className="model-info">
          <h3 className="model-name">{model.name}</h3>
          <code className="model-id">{model.id}</code>
        </div>
        <div className="model-badges">
          <span className={`badge badge-${model.tier === 'free' ? 'success' : 'info'}`}>
            {model.tier}
          </span>
          {model.speed && (
            <span className="badge badge-speed">{model.speed.replace('_', ' ')}</span>
          )}
          {!model.is_active && (
            <span className="badge badge-warning">Inactive</span>
          )}
          {model.is_deprecated && (
            <span className="badge badge-danger">Deprecated</span>
          )}
        </div>
      </div>

      {model.description && (
        <p className="model-description">{model.description}</p>
      )}

      <div className="model-details">
        {model.context_window && (
          <div className="detail-item">
            <span className="detail-label">Context</span>
            <span className="detail-value">{(model.context_window / 1000).toFixed(0)}K</span>
          </div>
        )}
        {model.max_output_tokens && (
          <div className="detail-item">
            <span className="detail-label">Max Output</span>
            <span className="detail-value">{(model.max_output_tokens / 1000).toFixed(0)}K</span>
          </div>
        )}
        {model.rate_limits?.rpm_limit && (
          <div className="detail-item">
            <span className="detail-label">RPM</span>
            <span className="detail-value">{model.rate_limits.rpm_limit}</span>
          </div>
        )}
        {model.rate_limits?.rpd_limit && (
          <div className="detail-item">
            <span className="detail-label">RPD</span>
            <span className="detail-value">{model.rate_limits.rpd_limit}</span>
          </div>
        )}
      </div>

      {capabilities.length > 0 && (
        <div className="model-capabilities">
          {capabilities.map(cap => (
            <span key={cap} className="capability-tag">{cap}</span>
          ))}
        </div>
      )}

      {(model.pricing?.input_per_million || model.input_price_per_million) && (
        <div className="model-pricing">
          <span className="pricing-label">Pricing:</span>
          <span className="pricing-value">
            ${model.pricing?.input_per_million || model.input_price_per_million} /
            ${model.pricing?.output_per_million || model.output_price_per_million} per 1M tokens
          </span>
        </div>
      )}

      <div className="model-actions">
        <button className="btn btn-sm btn-secondary" onClick={onEdit} disabled={isLoading}>
          Edit
        </button>
        <button
          className={`btn btn-sm ${model.is_active ? 'btn-warning' : 'btn-success'}`}
          onClick={onToggleActive}
          disabled={isLoading}
        >
          {isLoading ? '...' : model.is_active ? 'Deactivate' : 'Activate'}
        </button>
        <button
          className="btn btn-sm btn-danger"
          onClick={onDelete}
          disabled={isLoading}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function EditModelModal({ model, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: model.name || '',
    description: model.description || '',
    speed: model.speed || 'fast',
    tier: model.tier || 'paid',
    context_window: model.context_window || '',
    max_output_tokens: model.max_output_tokens || 4096,
    capabilities: model.capabilities || [],
    rpm_limit: model.rate_limits?.rpm_limit || model.rpm_limit || '',
    tpm_limit: model.rate_limits?.tpm_limit || model.tpm_limit || '',
    rpd_limit: model.rate_limits?.rpd_limit || model.rpd_limit || '',
    tpd_limit: model.rate_limits?.tpd_limit || model.tpd_limit || '',
    input_price_per_million: model.pricing?.input_per_million || model.input_price_per_million || '',
    output_price_per_million: model.pricing?.output_per_million || model.output_price_per_million || '',
    is_active: model.is_active,
    is_deprecated: model.is_deprecated || false,
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCapabilityToggle = (cap) => {
    setFormData(prev => ({
      ...prev,
      capabilities: prev.capabilities.includes(cap)
        ? prev.capabilities.filter(c => c !== cap)
        : [...prev.capabilities, cap]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        name: formData.name,
        description: formData.description,
        speed: formData.speed,
        tier: formData.tier,
        context_window: formData.context_window ? parseInt(formData.context_window) : null,
        max_output_tokens: formData.max_output_tokens ? parseInt(formData.max_output_tokens) : null,
        capabilities: formData.capabilities,
        rpm_limit: formData.rpm_limit ? parseInt(formData.rpm_limit) : null,
        tpm_limit: formData.tpm_limit ? parseInt(formData.tpm_limit) : null,
        rpd_limit: formData.rpd_limit ? parseInt(formData.rpd_limit) : null,
        tpd_limit: formData.tpd_limit ? parseInt(formData.tpd_limit) : null,
        input_price_per_million: formData.input_price_per_million ? parseFloat(formData.input_price_per_million) : null,
        output_price_per_million: formData.output_price_per_million ? parseFloat(formData.output_price_per_million) : null,
        is_active: formData.is_active,
        is_deprecated: formData.is_deprecated,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Edit Model: {model.name}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Model ID</label>
                <input type="text" className="form-input" value={model.id} disabled />
              </div>
              <div className="form-group">
                <label className="form-label">Provider</label>
                <input type="text" className="form-input" value={model.provider} disabled />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Display Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Speed</label>
                <select
                  className="form-input"
                  value={formData.speed}
                  onChange={(e) => handleChange('speed', e.target.value)}
                >
                  {SPEEDS.map(s => (
                    <option key={s} value={s}>{s.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-input"
                rows={2}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Tier</label>
                <select
                  className="form-input"
                  value={formData.tier}
                  onChange={(e) => handleChange('tier', e.target.value)}
                >
                  {TIERS.map(t => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Context Window</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.context_window}
                  onChange={(e) => handleChange('context_window', e.target.value)}
                  placeholder="e.g., 128000"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Max Output Tokens</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.max_output_tokens}
                  onChange={(e) => handleChange('max_output_tokens', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Capabilities</label>
              <div className="capabilities-grid">
                {CAPABILITIES.map(cap => (
                  <label key={cap} className="capability-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.capabilities.includes(cap)}
                      onChange={() => handleCapabilityToggle(cap)}
                    />
                    <span>{cap}</span>
                  </label>
                ))}
              </div>
            </div>

            <h4 className="section-subtitle">Rate Limits</h4>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">RPM (Requests/Min)</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.rpm_limit}
                  onChange={(e) => handleChange('rpm_limit', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">TPM (Tokens/Min)</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.tpm_limit}
                  onChange={(e) => handleChange('tpm_limit', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">RPD (Requests/Day)</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.rpd_limit}
                  onChange={(e) => handleChange('rpd_limit', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">TPD (Tokens/Day)</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.tpd_limit}
                  onChange={(e) => handleChange('tpd_limit', e.target.value)}
                />
              </div>
            </div>

            <h4 className="section-subtitle">Pricing (per 1M tokens)</h4>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Input Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={formData.input_price_per_million}
                  onChange={(e) => handleChange('input_price_per_million', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Output Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={formData.output_price_per_million}
                  onChange={(e) => handleChange('output_price_per_million', e.target.value)}
                />
              </div>
            </div>

            <h4 className="section-subtitle">Status</h4>
            <div className="form-row">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                />
                <span>Active (visible to users)</span>
              </label>
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={formData.is_deprecated}
                  onChange={(e) => handleChange('is_deprecated', e.target.checked)}
                />
                <span>Deprecated</span>
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateModelModal({ onClose, onSave }) {
  const [formData, setFormData] = useState({
    provider: 'openai',
    model_id: '',
    name: '',
    description: '',
    speed: 'fast',
    tier: 'paid',
    context_window: '',
    max_output_tokens: 4096,
    capabilities: ['text'],
    rpm_limit: '',
    tpm_limit: '',
    rpd_limit: '',
    tpd_limit: '',
    input_price_per_million: '',
    output_price_per_million: '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCapabilityToggle = (cap) => {
    setFormData(prev => ({
      ...prev,
      capabilities: prev.capabilities.includes(cap)
        ? prev.capabilities.filter(c => c !== cap)
        : [...prev.capabilities, cap]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const modelId = `${formData.provider}/${formData.model_id}`;
      await onSave({
        id: modelId,
        provider: formData.provider,
        model_id: formData.model_id,
        name: formData.name,
        description: formData.description,
        speed: formData.speed,
        tier: formData.tier,
        context_window: formData.context_window ? parseInt(formData.context_window) : null,
        max_output_tokens: formData.max_output_tokens ? parseInt(formData.max_output_tokens) : null,
        capabilities: formData.capabilities,
        rpm_limit: formData.rpm_limit ? parseInt(formData.rpm_limit) : null,
        tpm_limit: formData.tpm_limit ? parseInt(formData.tpm_limit) : null,
        rpd_limit: formData.rpd_limit ? parseInt(formData.rpd_limit) : null,
        tpd_limit: formData.tpd_limit ? parseInt(formData.tpd_limit) : null,
        input_price_per_million: formData.input_price_per_million ? parseFloat(formData.input_price_per_million) : null,
        output_price_per_million: formData.output_price_per_million ? parseFloat(formData.output_price_per_million) : null,
        is_active: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Add New Model</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Provider *</label>
                <select
                  className="form-input"
                  value={formData.provider}
                  onChange={(e) => handleChange('provider', e.target.value)}
                  required
                >
                  {PROVIDERS.map(p => (
                    <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Model ID *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.model_id}
                  onChange={(e) => handleChange('model_id', e.target.value)}
                  placeholder="e.g., gpt-5-turbo"
                  required
                />
                <small className="form-hint">Full ID will be: {formData.provider}/{formData.model_id || '...'}</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Display Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g., GPT-5 Turbo"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Speed</label>
                <select
                  className="form-input"
                  value={formData.speed}
                  onChange={(e) => handleChange('speed', e.target.value)}
                >
                  {SPEEDS.map(s => (
                    <option key={s} value={s}>{s.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-input"
                rows={2}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Brief description of the model's capabilities..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Tier</label>
                <select
                  className="form-input"
                  value={formData.tier}
                  onChange={(e) => handleChange('tier', e.target.value)}
                >
                  {TIERS.map(t => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Context Window</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.context_window}
                  onChange={(e) => handleChange('context_window', e.target.value)}
                  placeholder="e.g., 128000"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Max Output Tokens</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.max_output_tokens}
                  onChange={(e) => handleChange('max_output_tokens', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Capabilities</label>
              <div className="capabilities-grid">
                {CAPABILITIES.map(cap => (
                  <label key={cap} className="capability-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.capabilities.includes(cap)}
                      onChange={() => handleCapabilityToggle(cap)}
                    />
                    <span>{cap}</span>
                  </label>
                ))}
              </div>
            </div>

            <h4 className="section-subtitle">Rate Limits (optional)</h4>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">RPM</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.rpm_limit}
                  onChange={(e) => handleChange('rpm_limit', e.target.value)}
                  placeholder="Requests/min"
                />
              </div>
              <div className="form-group">
                <label className="form-label">TPM</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.tpm_limit}
                  onChange={(e) => handleChange('tpm_limit', e.target.value)}
                  placeholder="Tokens/min"
                />
              </div>
              <div className="form-group">
                <label className="form-label">RPD</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.rpd_limit}
                  onChange={(e) => handleChange('rpd_limit', e.target.value)}
                  placeholder="Requests/day"
                />
              </div>
              <div className="form-group">
                <label className="form-label">TPD</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.tpd_limit}
                  onChange={(e) => handleChange('tpd_limit', e.target.value)}
                  placeholder="Tokens/day"
                />
              </div>
            </div>

            <h4 className="section-subtitle">Pricing (per 1M tokens, optional)</h4>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Input Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={formData.input_price_per_million}
                  onChange={(e) => handleChange('input_price_per_million', e.target.value)}
                  placeholder="e.g., 2.50"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Output Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={formData.output_price_per_million}
                  onChange={(e) => handleChange('output_price_per_million', e.target.value)}
                  placeholder="e.g., 10.00"
                />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Creating...' : 'Create Model'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Icons
function SearchIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function ProviderIcon({ provider }) {
  const colors = {
    google: '#4285F4',
    openai: '#10a37f',
    claude: '#cc785c',
    groq: '#f55036',
    perplexity: '#20808d',
  };

  return (
    <span className="provider-icon" style={{ backgroundColor: colors[provider] || '#6366f1' }}>
      {provider?.[0]?.toUpperCase() || '?'}
    </span>
  );
}

export default Models;
